import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, useRoutes, type RouteObject } from 'react-router';

import { Splash } from '../components/ui/splash';
import { ActivitiesScreen } from '../screens/activities-screen';
import { ChatsScreen } from '../screens/chats-screen';
import { ProfileScreen } from '../screens/profile-screen';
import { Layout } from './layout';
import {
	BLOCKED_PATH,
	NOTIFICATIONS_PATH,
	NOTIFICATION_SETTINGS_PATH,
	EVENT_APPLICATIONS_PATH,
	EVENT_MANAGE_PATH,
	EVENT_PATH,
	PROFILE_EDIT_PATH,
	TAB_PATHS,
	USER_PATH,
	type Tab,
} from './paths';

// MapLibre is large: the map screen is its own chunk (DESIGN, JS budget).
const MapScreen = lazy(() => import('../screens/map-screen'));
const EventScreen = lazy(() => import('../screens/event-screen'));
const CreateScreen = lazy(() => import('../screens/create-screen'));
const ManageScreen = lazy(() => import('../screens/manage-screen'));
const ApplicationsScreen = lazy(() => import('../screens/applications-screen'));
const UserScreen = lazy(() => import('../screens/user-screen'));
const EditProfileScreen = lazy(() => import('../screens/edit-profile-screen'));
const BlockedScreen = lazy(() => import('../screens/blocked-screen'));
const NotificationsScreen = lazy(() => import('../screens/notifications-screen'));
const NotificationSettingsScreen = lazy(() => import('../screens/notification-settings-screen'));

function tabScreen(tab: Tab) {
	if (tab === 'activities') return <ActivitiesScreen />;
	if (tab === 'chats') return <ChatsScreen />;
	if (tab === 'profile') return <ProfileScreen />;
	if (tab === 'create') {
		return (
			<Suspense fallback={<Splash />}>
				<CreateScreen />
			</Suspense>
		);
	}
	return (
		<Suspense fallback={<Splash />}>
			<MapScreen />
		</Suspense>
	);
}

export const appRoutes: RouteObject[] = [
	{
		element: <Layout />,
		children: [
			{ index: true, element: <Navigate to={TAB_PATHS.map} replace /> },
			...(Object.keys(TAB_PATHS) as Tab[]).map((tab) => ({
				path: TAB_PATHS[tab],
				element: tabScreen(tab),
			})),
			{
				path: EVENT_MANAGE_PATH,
				element: (
					<Suspense fallback={<Splash />}>
						<ManageScreen />
					</Suspense>
				),
			},
			{
				path: USER_PATH,
				element: (
					<Suspense fallback={<Splash />}>
						<UserScreen />
					</Suspense>
				),
			},
			{
				path: NOTIFICATIONS_PATH,
				element: (
					<Suspense fallback={<Splash />}>
						<NotificationsScreen />
					</Suspense>
				),
			},
			{
				path: NOTIFICATION_SETTINGS_PATH,
				element: (
					<Suspense fallback={<Splash />}>
						<NotificationSettingsScreen />
					</Suspense>
				),
			},
			{
				path: BLOCKED_PATH,
				element: (
					<Suspense fallback={<Splash />}>
						<BlockedScreen />
					</Suspense>
				),
			},
			{
				path: PROFILE_EDIT_PATH,
				element: (
					<Suspense fallback={<Splash />}>
						<EditProfileScreen />
					</Suspense>
				),
			},
			{
				path: EVENT_APPLICATIONS_PATH,
				element: (
					<Suspense fallback={<Splash />}>
						<ApplicationsScreen />
					</Suspense>
				),
			},
			{
				path: EVENT_PATH,
				element: (
					<Suspense fallback={<Splash />}>
						<EventScreen />
					</Suspense>
				),
			},
			{ path: '*', element: <Navigate to={TAB_PATHS.map} replace /> },
		],
	},
];

function AppRoutes() {
	return useRoutes(appRoutes);
}

/** Declarative router: data is loaded with TanStack Query, so router loaders are not needed. */
export function AppRouter() {
	return (
		<BrowserRouter>
			<AppRoutes />
		</BrowserRouter>
	);
}
